import { Timestamp } from "firebase/firestore";
import { deepTransform } from "@repo/lib/deepTransform";

interface DeepDateOptions {
  /** Default: true */
  jsonTimestamps?: boolean;
  /** Default: false */
  isoStrings?: boolean;
}

const MS_TO_NANOS = 1000000;
export const tryConvertJSONTimestamp = (value: unknown) => {
  interface JSONTimestamp {
    _seconds: number;
    _nanoseconds: number;
  }

  if (!value || typeof value !== "object") return null;

  // detect JSON-flattened Timestamp object
  const timestamp = value as JSONTimestamp;
  const keys = Object.keys(timestamp);
  if (
    keys.length === 2 &&
    keys.includes("_seconds") &&
    keys.includes("_nanoseconds") &&
    typeof timestamp._seconds === "number" &&
    typeof timestamp._nanoseconds === "number"
  ) {
    const { _seconds, _nanoseconds } = timestamp;
    return new Date(_seconds * 1000 + Math.round(_nanoseconds / MS_TO_NANOS));
  }

  return null;
};

export const tryConvertISOString = (value: unknown) => {
  if (!value || typeof value !== "string") return null;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value)) return;

  return new Date(value);
};

/** Deeply find and convert all Firestore Timestamps into Date objects. */
export const deepFirestoreToDateTime = <T>(
  object: T,
  { jsonTimestamps = true, isoStrings = false }: DeepDateOptions = {}
) =>
  deepTransform(
    object,
    (value) => {
      // detect Timestamp instance
      if (value instanceof Timestamp) {
        return value.toDate();
      }

      const date =
        (jsonTimestamps && tryConvertJSONTimestamp(value)) ||
        (isoStrings && tryConvertISOString(value));
      if (date) return date;

      return deepTransform.preserveField;
    },
    { testObjects: true }
  ) as T;

/** Deeply find and convert all Date objects into Firestore Timestamps. */
export const deepDateTimeToFirestore = <T>(
  object: T,
  { jsonTimestamps = true, isoStrings = false }: DeepDateOptions = {}
) =>
  deepTransform(object, (value) => {
    if (value instanceof Date) {
      return Timestamp.fromDate(value);
    }
    const date =
      (jsonTimestamps && tryConvertJSONTimestamp(value)) ||
      (isoStrings && tryConvertISOString(value));
    if (date) return Timestamp.fromDate(date);

    return deepTransform.preserveField;
  }) as T;
